import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import ImageDropUpload from "../components/ImageDropUpload";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, FileText, Plus, Save, Trash2, X } from "lucide-react";

const createEmptyInsight = (sortOrder) => ({
    title: "",
    tag: "",
    cover: "",
    teaser: "",
    body: "",
    enabled: true,
    sort_order: sortOrder,
});

export default function InsightsPage() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(createEmptyInsight(0));
    const [editing, setEditing] = useState("");
    const [saving, setSaving] = useState(false);
    const sortedItems = useMemo(() => [...items].sort((a, b) => (
        a.sort_order - b.sort_order || (a.created_at || "").localeCompare(b.created_at || "")
    )), [items]);
    const nextSortOrder = useMemo(() => sortedItems.length, [sortedItems]);
    const load = async () => {
        const { data } = await api.get("/insights");
        setItems(data);
        return data;
    };

    useEffect(() => {
        load().catch(() => toast.error("资讯加载失败"));
    }, []);

    const startNew = () => {
        setEditing("");
        setForm(createEmptyInsight(nextSortOrder));
    };

    const startEdit = (item) => {
        setEditing(item.id);
        setForm({
            title: item.title || "",
            tag: item.tag || "",
            cover: item.cover || "",
            teaser: item.teaser || "",
            body: item.body || "",
            enabled: item.enabled !== false,
            sort_order: item.sort_order || 0,
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            let saved;
            if (editing) {
                ({ data: saved } = await api.put(`/insights/${editing}`, form));
            } else {
                ({ data: saved } = await api.post("/insights", form));
            }
            const freshItems = await load();
            const currentIndex = freshItems.findIndex((item) => item.id === saved.id);
            const targetIndex = Math.min(Math.max(form.sort_order, 0), freshItems.length - 1);
            if (currentIndex !== targetIndex) {
                const reordered = [...freshItems];
                const [moved] = reordered.splice(currentIndex, 1);
                reordered.splice(targetIndex, 0, moved);
                await saveOrder(reordered);
            }
            toast.success("文章已保存");
            setEditing("");
            setForm(createEmptyInsight(freshItems.length));
        } catch (error) {
            toast.error(error.response?.data?.detail || "保存失败");
        } finally {
            setSaving(false);
        }
    };

    const saveOrder = async (orderedItems) => {
        const normalized = orderedItems.map((item, index) => ({ ...item, sort_order: index }));
        setItems(normalized);
        await api.put("/insights/sort-order", {
            items: normalized.map((item) => ({ id: item.id, sort_order: item.sort_order })),
        });
        return normalized;
    };

    const remove = async (item) => {
        if (!window.confirm(`确定删除「${item.title}」吗？`)) {
            return;
        }
        try {
            await api.delete(`/insights/${item.id}`);
            if (editing === item.id) {
                startNew();
            }
            await load();
            toast.success("文章已删除");
        } catch (error) {
            toast.error(error.response?.data?.detail || "删除失败");
        }
    };

    const reorder = async (fromIndex, toIndex) => {
        if (toIndex < 0 || toIndex >= sortedItems.length) {
            return;
        }
        const next = [...sortedItems];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        try {
            await saveOrder(next);
            toast.success("文章顺序已更新");
        } catch (error) {
            toast.error(error.response?.data?.detail || "排序保存失败");
            await load();
        }
    };

    return (
        <div className="space-y-6" data-testid="insights-page">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gold-500">
                        落地页
                    </div>
                    <h1 className="mt-1 text-3xl font-bold text-slate-900">资讯管理</h1>
                    <p className="mt-1 text-sm text-gold-600">
                        上架文章会自动显示在落地页「福缘资讯」板块。
                    </p>
                </div>
                <Button
                    type="button"
                    data-testid="insight-new-button"
                    onClick={startNew}
                    className="gap-2 bg-gold-600 text-white hover:bg-gold-700"
                >
                    <Plus className="h-4 w-4" /> 新增文章
                </Button>
            </div>
            <div
                className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]"
            >
                <Card className="border-slate-200 p-5 shadow-sm">
                    <div className="mb-4 flex items-center justify-between">
                        <h2
                            className={[
                                "text-sm font-semibold uppercase",
                                "tracking-wider text-gold-600",
                            ].join(" ")}
                        >
                            文章列表
                        </h2>
                        <Badge className="border-0 bg-gold-100 text-gold-800">
                            {sortedItems.length} 篇
                        </Badge>
                    </div>
                    {sortedItems.length === 0 ? (
                        <div className="py-12 text-center text-sm text-slate-500">
                            还没有文章。新增第一篇资讯后，落地页会自动显示此板块。
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {sortedItems.map((item, index) => (
                                <div
                                    key={item.id}
                                    data-testid={`insight-row-${item.id}`}
                                    className={`flex items-center gap-3 rounded-md border p-3 ${
                                        editing === item.id
                                            ? "border-gold-400 bg-gold-50"
                                            : "border-slate-200 bg-white"
                                    }`}
                                >
                                    <div className="flex shrink-0 flex-col">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            data-testid={`insight-move-up-${item.id}`}
                                            disabled={index === 0}
                                            onClick={() => reorder(index, index - 1)}
                                            className="h-6 w-7 p-0 text-gold-600"
                                            title="向前移动"
                                        >
                                            <ArrowUp className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            data-testid={`insight-move-down-${item.id}`}
                                            disabled={index === sortedItems.length - 1}
                                            onClick={() => reorder(index, index + 1)}
                                            className="h-6 w-7 p-0 text-gold-600"
                                            title="向后移动"
                                        >
                                            <ArrowDown className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div
                                            className={[
                                                "truncate text-sm font-semibold",
                                                "text-slate-900",
                                            ].join(" ")}
                                        >
                                            {item.title}
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                            {item.tag && (
                                                <Badge
                                                    className="border-0 bg-slate-100 text-slate-600"
                                                >
                                                    {item.tag}
                                                </Badge>
                                            )}
                                            {item.tag && (
                                                <span className="text-xs text-slate-300">•</span>
                                            )}
                                            <span className="text-xs text-gold-600">
                                                排序 {item.sort_order}
                                            </span>
                                            <span className="text-xs text-slate-300">•</span>
                                            <span
                                                className={`text-xs ${
                                                    item.enabled
                                                        ? "text-emerald-600"
                                                        : "text-slate-400"
                                                }`}
                                            >
                                                {item.enabled ? "已上架" : "草稿"}
                                            </span>
                                        </div>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        data-testid={`insight-edit-${item.id}`}
                                        onClick={() => startEdit(item)}
                                    >
                                        编辑
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        data-testid={`insight-delete-${item.id}`}
                                        onClick={() => remove(item)}
                                        className="text-red-600 hover:bg-red-50"
                                        title="删除文章"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
                <Card className="border-slate-200 p-6 shadow-sm">
                    <div className="mb-5 flex items-center justify-between">
                        <div>
                            <h2
                                className={[
                                    "flex items-center gap-2 text-sm",
                                    "font-semibold uppercase tracking-wider text-gold-600",
                                ].join(" ")}
                            >
                                <FileText className="h-4 w-4" />
                                {editing ? "编辑文章" : "新增文章"}
                            </h2>
                            <p className="mt-1 text-xs text-slate-500">
                                排序 0 会最先显示；新文章默认自动排在最后。
                            </p>
                        </div>
                        {editing && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                data-testid="insight-cancel-edit"
                                onClick={startNew}
                                title="取消编辑"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                    <div className="space-y-4">
                        <Field
                            label="文章标题 *"
                            testId="insight-title"
                            value={form.title}
                            onChange={(title) => setForm({ ...form, title })}
                            placeholder="如：属龙 · 2026年运程"
                        />
                        <Field
                            label="标签"
                            testId="insight-tag"
                            value={form.tag}
                            onChange={(tag) => setForm({ ...form, tag })}
                            placeholder="如：生肖运程、选购心得、保养知识"
                        />
                        <div className="space-y-1.5">
                            <Label>封面图</Label>
                            <ImageDropUpload
                                value={form.cover}
                                onChange={(cover) => setForm({ ...form, cover })}
                                maxEdge={800}
                                previewClassName="aspect-square max-w-xs"
                                testIdPrefix="insight-cover"
                                helperText="JPG / PNG · 自动压缩至 800px · 建议正方形"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="insight-teaser">摘要</Label>
                            <Input
                                id="insight-teaser"
                                data-testid="insight-teaser"
                                value={form.teaser}
                                onChange={(event) => setForm({
                                    ...form,
                                    teaser: event.target.value,
                                })}
                                placeholder="一句话摘要（建议 30 字以内）"
                                maxLength={120}
                            />
                            <p className="text-right text-[10px] text-slate-400">
                                {form.teaser.length}/120
                            </p>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="insight-body">正文</Label>
                            <Textarea
                                id="insight-body"
                                data-testid="insight-body"
                                value={form.body}
                                onChange={(event) => setForm({
                                    ...form,
                                    body: event.target.value,
                                })}
                                placeholder="正文内容（可用换行分段）"
                                rows={10}
                            />
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label htmlFor="insight-sort">排序</Label>
                                <Input
                                    id="insight-sort"
                                    data-testid="insight-sort"
                                    type="number"
                                    min="0"
                                    value={form.sort_order}
                                    onChange={(event) => setForm({
                                        ...form,
                                        sort_order: Math.max(
                                            0,
                                            Number(event.target.value) || 0,
                                        ),
                                    })}
                                />
                            </div>
                            <div
                                className={[
                                    "flex items-end justify-between rounded-md border",
                                    "border-slate-200 bg-gold-50 p-3",
                                ].join(" ")}
                            >
                                <Label htmlFor="insight-enabled" className="text-sm">
                                    上架显示
                                </Label>
                                <Switch
                                    id="insight-enabled"
                                    data-testid="insight-enabled"
                                    checked={form.enabled}
                                    onCheckedChange={(enabled) => setForm({
                                        ...form,
                                        enabled,
                                    })}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                data-testid="insight-reset"
                                onClick={startNew}
                            >
                                重置
                            </Button>
                            <Button
                                type="button"
                                data-testid="insight-save"
                                disabled={saving}
                                onClick={save}
                                className="gap-2 bg-gold-600 text-white hover:bg-gold-700"
                            >
                                <Save className="h-4 w-4" />
                                {saving ? "保存中..." : "保存文章"}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}

function Field({ label, testId, value, onChange, placeholder }) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={testId}>{label}</Label>
            <Input
                id={testId}
                data-testid={testId}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
            />
        </div>
    );
}